import httpClient from "@utils/httpClient";

export interface AssessmentDomain {
  id: number;
  assessment_id: number;
  domain_name: string;
  created_at: string;
  updated_at: string;
  assessmentTitle?: string;
  course?: string;
  status?: string;
}

export interface DomainData {
  assessmentId?: number;
  totalDomains: number;
  domains: AssessmentDomain[];
}

export interface ApiResponse<T = any> {
  success: boolean;
  status?: string;
  data?: T;
  message?: string;
  error?: string | { message: string };
}

/**
 * Get all domains for a specific assessment
 */
export const getAssessmentDomains = async (
  assessmentId: number
): Promise<ApiResponse<DomainData>> => {
  try {
    const response = await httpClient.get(`/assessment-domains/assessment/${assessmentId}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('getAssessmentDomains error:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch assessment domains'
    };
  }
};

/**
 * Get all domains across all assessments
 */
export const getAllDomains = async (): Promise<ApiResponse<DomainData>> => {
  try {
    const response = await httpClient.get('/assessment-domains/all');
    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('getAllDomains error:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch domains'
    };
  }
};

/**
 * Add a new domain for an assessment
 */
export const addDomain = async (
  assessmentId: number,
  domainName: string
): Promise<ApiResponse> => {
  try {
    const response = await httpClient.post('/assessment-domains', {
      assessmentId,
      domainName
    });
    return {
      success: true,
      message: response.message || 'Domain added successfully',
      data: response.data
    };
  } catch (error: any) {
    console.error('addDomain error:', error);
    return {
      success: false,
      error: error.message || 'Failed to add domain'
    };
  }
};

/**
 * Bulk add domains for an assessment
 */
export const bulkAddDomains = async (
  assessmentId: number,
  domains: string[]
): Promise<ApiResponse> => {
  try {
    const response = await httpClient.post('/assessment-domains/bulk', {
      assessmentId,
      domains
    });
    return {
      success: true,
      message: response.message || 'Domains added successfully',
      data: response.data
    };
  } catch (error: any) {
    console.error('bulkAddDomains error:', error);
    return {
      success: false,
      error: error.message || 'Failed to add domains'
    };
  }
};

/**
 * Update a domain
 */
export const updateDomain = async (
  id: number,
  domainName: string
): Promise<ApiResponse> => {
  try {
    const response = await httpClient.put(`/assessment-domains/${id}`, {
      domainName
    });
    return {
      success: true,
      message: response.message || 'Domain updated successfully',
      data: response.data
    };
  } catch (error: any) {
    console.error('updateDomain error:', error);
    return {
      success: false,
      error: error.message || 'Failed to update domain'
    };
  }
};

/**
 * Delete a domain
 */
export const deleteDomain = async (
  id: number
): Promise<ApiResponse> => {
  try {
    const response = await httpClient.delete(`/assessment-domains/${id}`);
    return {
      success: true,
      message: response.message || 'Domain deleted successfully'
    };
  } catch (error: any) {
    console.error('deleteDomain error:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete domain'
    };
  }
};
